import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionPlan, FREE_PRODUCT_LIMIT } from "@/hooks/useSubscriptionPlan";
import { usePaywallModal } from "@/contexts/PaywallModalContext";
import { useBranchContext } from "@/contexts/BranchContext";
import { useBranches } from "@/hooks/useBranches";
import { clearPaywallPreviewCache } from "@/hooks/usePaywallPreview";
import { ANGEL_BUSINESS_ID } from "@/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Package, AlertTriangle, Trash2, ArrowRightLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NoMatchingRecords } from "@/components/NoMatchingRecords";
import { cn } from "@/lib/utils";
import { useInvalidateReorderQueries } from "@/hooks/useReorderIntelligence";

/** Matches DB exactly. Optional fields exist only after migration (selling_price, category, buying_price, branch_id). */
type InventoryRow = {
  id: string;
  name: string;
  sku: string;
  current_stock: number;
  min_threshold: number;
  business_id: string | null;
  branch_id?: string | null;
  selling_price?: number;
  category?: string | null;
  buying_price?: number | null;
};

export default function InventoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, businessId, isFounder, role, refreshProfile } = useAuth();
  const isReviewboostsServices = user?.email?.toLowerCase().trim() === "reviewboosts.services@gmail.com";
  const subscriptionPlan = useSubscriptionPlan();
  const { openPaywall } = usePaywallModal();
  const { branches, canAddBranch } = useBranches();
  const { selectedBranchId, setSelectedBranchId } = useBranchContext();
  const [inventory, setInventory] = useState<InventoryRow[]>([]);
  const branchParam = searchParams.get("branch");
  const deadStockIds = useMemo(
    () => searchParams.get("deadStock")?.split(",").filter(Boolean) ?? [],
    [searchParams]
  );
  const highlightItemId = searchParams.get("item") ?? null;
  const highlightRowRef = useRef<HTMLTableRowElement>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<InventoryRow | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [schemaHasOptionalColumns, setSchemaHasOptionalColumns] = useState(true);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);
  const { toast } = useToast();
  const invalidateReorderQueries = useInvalidateReorderQueries();

  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [stock, setStock] = useState(0);
  const [threshold, setThreshold] = useState(5);
  const [sellingPrice, setSellingPrice] = useState(0);
  const [category, setCategory] = useState("");
  const [buyingPrice, setBuyingPrice] = useState<number | "">("");
  const [buyingPriceTouched, setBuyingPriceTouched] = useState(false);
  const [addBranchId, setAddBranchId] = useState<string | null>(null);
  const [transferItem, setTransferItem] = useState<InventoryRow | null>(null);
  const [transferToBranchId, setTransferToBranchId] = useState("");
  const [transferQty, setTransferQty] = useState(1);
  const [transferring, setTransferring] = useState(false);

  const isAdmin = role === "admin" || isFounder;

  const fetchInventory = async (overrideBusinessId?: string | null) => {
    const effectiveBizId = overrideBusinessId ?? businessId;
    if (!effectiveBizId) return;
    setFetchError(null);

    // Full schema (after migration): includes buying_price, selling_price, category, branch_id
    const fullSelect = "id, name, sku, current_stock, min_threshold, business_id, branch_id, selling_price, category, buying_price";
    const legacySelect = "id, name, sku, current_stock, min_threshold, business_id";

    const baseQueryFull = supabase.from("inventory").select(fullSelect).order("name").eq("business_id", effectiveBizId);
    const { data: dataFull, error: errorFull } = await baseQueryFull;

    const isSchemaError = errorFull && (
      errorFull.message?.includes("buying_price") ||
      errorFull.message?.includes("selling_price") ||
      errorFull.message?.includes("category") ||
      errorFull.message?.includes("branch_id") ||
      errorFull.message?.includes("column") ||
      errorFull.message?.includes("does not exist")
    );
    if (errorFull && isSchemaError) {
      setSchemaHasOptionalColumns(false);
      const baseQueryLegacy = supabase.from("inventory").select(legacySelect).order("name").eq("business_id", effectiveBizId);
      const { data: dataLegacy, error: errorLegacy } = await baseQueryLegacy;
      if (errorLegacy) {
        toast({ title: "Error loading inventory", description: errorLegacy.message, variant: "destructive" });
        setInventory([]);
        return;
      }
      setInventory((dataLegacy ?? []) as InventoryRow[]);
      setFetchError("Run migration to add selling_price, category, and buying_price (see migration file).");
      return;
    }

    if (errorFull) {
      toast({ title: "Error loading inventory", description: errorFull.message, variant: "destructive" });
      setInventory([]);
      return;
    }

    setSchemaHasOptionalColumns(true);
    if (dataFull) setInventory(dataFull as InventoryRow[]);
  };

  useEffect(() => {
    fetchInventory();
  }, [businessId]);

  useEffect(() => {
    if (branchParam && branches.some((b) => b.id === branchParam)) setSelectedBranchId(branchParam);
  }, [branchParam, branches, setSelectedBranchId]);

  useEffect(() => {
    if (searchParams.get("openAdd") === "1") {
      setAddOpen(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("openAdd");
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const hasWrongBusiness = isReviewboostsServices && businessId === ANGEL_BUSINESS_ID;
    const needEnsure =
      isReviewboostsServices && !isFounder ||
      (!businessId && !isFounder && (role === "admin" || isReviewboostsServices)) ||
      (isReviewboostsServices && !isFounder && (!businessId || hasWrongBusiness));
    if (needEnsure) {
      if (subscriptionPlan === "free" && inventory.length >= FREE_PRODUCT_LIMIT) {
        openPaywall("product_limit");
        return;
      }
      const { data: result, error: rpcError } = await supabase.rpc("insert_inventory_for_self", {
        p_name: name,
        p_sku: sku,
        p_current_stock: stock,
        p_min_threshold: threshold,
        p_selling_price: schemaHasOptionalColumns ? Number(sellingPrice) || 0 : null,
        p_category: category || null,
        p_buying_price: buyingPrice === "" ? null : (Number(buyingPrice) || null),
        p_branch_id: addBranchId || undefined,
      });
      if (rpcError) {
        toast({ title: "Error", description: rpcError.message || "Could not add item.", variant: "destructive" });
        return;
      }
      const res = result as { id?: string; business_id?: string; branch_id?: string | null } | null;
      if (res?.id && res?.business_id) {
        const newRow: InventoryRow = {
          id: res.id,
          name,
          sku,
          current_stock: stock,
          min_threshold: threshold,
          business_id: res.business_id,
          branch_id: res.branch_id ?? undefined,
          selling_price: schemaHasOptionalColumns ? Number(sellingPrice) || 0 : undefined,
          category: category || undefined,
          buying_price: buyingPrice === "" ? undefined : (Number(buyingPrice) ?? undefined),
        };
        setInventory((prev) => [newRow, ...prev.filter((x) => x.id !== res.id)]);
        await refreshProfile();
        clearPaywallPreviewCache();
        setSuccessBanner("Item added successfully");
        setTimeout(() => setSuccessBanner(null), 4000);
        toast({ title: "Item Added", description: `${name} has been added to inventory.` });
        setName(""); setSku(""); setStock(0); setThreshold(5); setSellingPrice(0); setCategory(""); setBuyingPrice("");
        setAddOpen(false);
        fetchInventory(res.business_id);
        setTimeout(() => fetchInventory(res.business_id), 800);
      }
      return;
    }
    if (!businessId) {
      toast({
        title: "No business",
        description: "Your account is not linked to a business.",
        variant: "destructive",
      });
      return;
    }
    if (subscriptionPlan === "free" && inventory.length >= FREE_PRODUCT_LIMIT) {
      openPaywall("product_limit");
      return;
    }
    const basePayload = {
      name,
      sku,
      current_stock: stock,
      min_threshold: threshold,
      business_id: businessId ?? null,
      branch_id: addBranchId || null,
    };
    const payload = schemaHasOptionalColumns
      ? {
          ...basePayload,
          selling_price: Number(sellingPrice) || 0,
          category: category || null,
          buying_price: buyingPrice === "" ? null : Number(buyingPrice),
        }
      : basePayload;
    const { error } = await supabase.from("inventory").insert(payload);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setSuccessBanner("Item added successfully");
      setTimeout(() => setSuccessBanner(null), 4000);
      toast({ title: "Item Added", description: `${name} has been added to inventory.` });
      setName(""); setSku(""); setStock(0); setThreshold(5); setSellingPrice(0); setCategory(""); setBuyingPrice("");
      setAddOpen(false);
      fetchInventory();
    }
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editItem) return;
    const payload: {
      name?: string;
      sku?: string;
      current_stock?: number;
      min_threshold?: number;
      selling_price?: number;
      category?: string | null;
      buying_price?: number | null;
    } = {};
    if (isAdmin) {
      payload.name = name;
      payload.sku = sku;
      payload.current_stock = stock;
      payload.min_threshold = threshold;
      if (schemaHasOptionalColumns) {
        payload.selling_price = Number(sellingPrice) || 0;
        payload.category = category || null;
        // Preserve current buying price unless user intentionally edits it.
        if (buyingPriceTouched) {
          payload.buying_price = buyingPrice === "" ? null : Number(buyingPrice);
        }
      }
    } else {
      payload.current_stock = stock;
      payload.min_threshold = threshold;
    }
    const { error } = await supabase.from("inventory").update(payload).eq("id", editItem.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      if (businessId) {
        await supabase.rpc("calculate_reorder_intelligence", { p_business_id: businessId });
      }
      invalidateReorderQueries();
      toast({ title: isAdmin ? "Item Updated" : "Stock Updated" });
      setEditItem(null);
      fetchInventory();
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("inventory").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Item Deleted" });
      fetchInventory();
    }
  };

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transferItem?.branch_id || !transferToBranchId || transferQty < 1) return;
    setTransferring(true);
    const { error } = await supabase.rpc("transfer_stock", {
      p_inventory_id: transferItem.id,
      p_from_branch_id: transferItem.branch_id,
      p_to_branch_id: transferToBranchId,
      p_quantity: transferQty,
    });
    setTransferring(false);
    if (error) {
      toast({ title: "Transfer failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Transfer done", description: `${transferQty} unit(s) moved.` });
    setTransferItem(null);
    setTransferToBranchId("");
    setTransferQty(1);
    fetchInventory();
  };

  useEffect(() => {
    if (highlightItemId && highlightRowRef.current) {
      highlightRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightItemId, inventory]);

  const filteredInventory = useMemo(() => {
    let list = inventory;
    if (selectedBranchId !== "all") list = list.filter((i) => i.branch_id === selectedBranchId);
    if (deadStockIds.length > 0) list = list.filter((i) => deadStockIds.includes(i.id));
    return list;
  }, [inventory, deadStockIds, selectedBranchId]);

  const branchNameById = useMemo(() => {
    const m: Record<string, string> = {};
    branches.forEach((b) => { m[b.id] = b.name; });
    return m;
  }, [branches]);

  return (
    <div className="space-y-6">
      {successBanner && (
        <div className="rounded-lg border border-emerald-500/50 bg-emerald-500/10 px-4 py-3 text-sm font-medium text-emerald-700 dark:text-emerald-300">
          {successBanner}
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-display font-bold">Inventory Command</h2>
          <p className="text-sm text-muted-foreground">
            {deadStockIds.length > 0
              ? `Showing dead stock items only (${deadStockIds.length} items) — adjust prices below`
              : "Manage your items and thresholds"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {branches.length > 1 && (
            <Select value={selectedBranchId} onValueChange={(v) => setSelectedBranchId(v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        <Dialog open={addOpen} onOpenChange={(open) => { setAddOpen(open); if (!open) setAddBranchId(null); else if (branches.length > 0 && !addBranchId) setAddBranchId(selectedBranchId !== "all" ? selectedBranchId : branches[0]?.id ?? null); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" /> Add New Item
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Item</DialogTitle>
              {!schemaHasOptionalColumns && (
                <p className="text-xs text-muted-foreground">Run migration to add selling_price, category, and buying_price.</p>
              )}
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4">
              {branches.length > 0 && (
                <div>
                  <Label>Location</Label>
                  <Select value={addBranchId ?? ""} onValueChange={(v) => setAddBranchId(v || null)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required /></div>
              <div><Label>SKU</Label><Input value={sku} onChange={(e) => setSku(e.target.value)} required /></div>
              {schemaHasOptionalColumns && (
                <>
                  <div><Label>Category</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Beverages" /></div>
                  <div><Label>Selling Price</Label><Input type="number" step="0.01" min={0} value={sellingPrice} onChange={(e) => setSellingPrice(Number(e.target.value) || 0)} /></div>
                  {isAdmin && <div><Label>Buying Price (private)</Label><Input type="number" step="0.01" min={0} value={buyingPrice} onChange={(e) => setBuyingPrice(e.target.value === "" ? "" : Number(e.target.value))} placeholder="Optional" /></div>}
                </>
              )}
              <div><Label>Initial Stock</Label><Input type="number" value={stock} onChange={(e) => setStock(+e.target.value)} min={0} required /></div>
              <div><Label>Min Threshold</Label><Input type="number" value={threshold} onChange={(e) => setThreshold(+e.target.value)} min={0} required /></div>
              <Button type="submit" className="w-full bg-primary text-primary-foreground">Add Item</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4 text-primary" /> All Items
          </CardTitle>
          {fetchError && (
            <p className="text-sm text-muted-foreground">{fetchError}</p>
          )}
        </CardHeader>
        <CardContent>
          {filteredInventory.length === 0 ? (
            inventory.length === 0 && !fetchError ? (
              <p className="text-muted-foreground text-sm py-4 text-center">
                No items in inventory yet. Use <strong>Add New Item</strong> above to add your first item (name, SKU, category, selling/buying price, stock).
              </p>
            ) : fetchError ? (
              <p className="text-muted-foreground text-sm py-4 text-center">Fix the schema error above, then refresh.</p>
            ) : (
              <NoMatchingRecords />
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {branches.length > 1 && <th className="text-left py-3 px-2 font-semibold">Location</th>}
                    <th className="text-left py-3 px-2 font-semibold">Name</th>
                    <th className="text-left py-3 px-2 font-semibold">SKU</th>
                    {isAdmin && schemaHasOptionalColumns && <th className="text-left py-3 px-2 font-semibold">Category</th>}
                    {isAdmin && schemaHasOptionalColumns && <th className="text-right py-3 px-2 font-semibold">Selling</th>}
                    {isAdmin && schemaHasOptionalColumns && <th className="text-right py-3 px-2 font-semibold">Buying</th>}
                    <th className="text-right py-3 px-2 font-semibold">Stock</th>
                    <th className="text-right py-3 px-2 font-semibold">Min Threshold</th>
                    <th className="text-right py-3 px-2 font-semibold">Status</th>
                    <th className="text-right py-3 px-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.map((item) => {
                  const isCritical = item.current_stock <= item.min_threshold;
                  const isHighlighted = highlightItemId === item.id;
                  return (
                    <tr
                      key={item.id}
                      ref={isHighlighted ? highlightRowRef : undefined}
                      className={cn(
                        "border-b border-border/50",
                        isCritical && "bg-critical/5",
                        isHighlighted && "ring-2 ring-primary ring-inset"
                      )}
                    >
                      {branches.length > 1 && (
                        <td className="py-3 px-2 text-muted-foreground">{branchNameById[item.branch_id ?? ""] ?? "—"}</td>
                      )}
                      <td className="py-3 px-2 font-medium">{item.name}</td>
                      <td className="py-3 px-2 text-muted-foreground">{item.sku}</td>
                      {isAdmin && schemaHasOptionalColumns && <td className="py-3 px-2 text-muted-foreground">{item.category ?? "—"}</td>}
                      {isAdmin && schemaHasOptionalColumns && <td className="py-3 px-2 text-right">{(item.selling_price ?? 0).toFixed(2)}</td>}
                      {isAdmin && schemaHasOptionalColumns && <td className="py-3 px-2 text-right">{item.buying_price != null ? item.buying_price.toFixed(2) : "—"}</td>}
                      <td className={`py-3 px-2 text-right font-bold ${isCritical ? "text-critical" : ""}`}>{item.current_stock}</td>
                      <td className="py-3 px-2 text-right text-muted-foreground">{item.min_threshold}</td>
                      <td className="py-3 px-2 text-right">
                        {isCritical ? (
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="h-3 w-3" /> Critical
                          </Badge>
                        ) : (
                          <Badge className="bg-primary/20 text-primary-foreground border-primary/30">OK</Badge>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex gap-1 justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditItem(item);
                            setName(item.name);
                            setSku(item.sku);
                            setStock(item.current_stock);
                            setThreshold(item.min_threshold);
                            setSellingPrice(item.selling_price ?? 0);
                            setCategory(item.category ?? "");
                            setBuyingPrice(item.buying_price ?? "");
                            setBuyingPriceTouched(false);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {isAdmin && branches.length > 1 && item.branch_id && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setTransferItem(item);
                              setTransferToBranchId(branches.find((b) => b.id !== item.branch_id)?.id ?? "");
                              setTransferQty(item.current_stock >= 1 ? 1 : 0);
                            }}
                            title="Transfer to another location"
                          >
                            <ArrowRightLeft className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(item.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editItem} onOpenChange={(open) => { if (!open) setEditItem(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isAdmin ? "Edit Item" : "Update Stock"} — {editItem?.name}</DialogTitle></DialogHeader>
          <form onSubmit={handleEditSave} className="space-y-4">
            {isAdmin && (
              <>
                <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required /></div>
                <div><Label>SKU</Label><Input value={sku} onChange={(e) => setSku(e.target.value)} required /></div>
                {schemaHasOptionalColumns && (
                  <>
                    <div><Label>Category</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} /></div>
                    <div><Label>Selling Price</Label><Input type="number" step="0.01" min={0} value={sellingPrice} onChange={(e) => setSellingPrice(Number(e.target.value) || 0)} /></div>
                    <div><Label>Buying Price (private)</Label><Input type="number" step="0.01" min={0} value={buyingPrice} onChange={(e) => { setBuyingPriceTouched(true); setBuyingPrice(e.target.value === "" ? "" : Number(e.target.value)); }} placeholder="Optional" /></div>
                  </>
                )}
              </>
            )}
            <div><Label>Current Stock (stock_level)</Label><Input type="number" value={stock} onChange={(e) => setStock(+e.target.value)} min={0} required /></div>
            <div><Label>Min Threshold</Label><Input type="number" value={threshold} onChange={(e) => setThreshold(+e.target.value)} min={0} required /></div>
            <Button type="submit" className="w-full bg-primary text-primary-foreground">Save</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!transferItem} onOpenChange={(open) => { if (!open) setTransferItem(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer stock</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {transferItem && (
                <>"{transferItem.name}" from {branchNameById[transferItem.branch_id ?? ""] ?? "this location"} · Max {transferItem.current_stock}</>
              )}
            </p>
            </DialogHeader>
            <form onSubmit={handleTransfer} className="space-y-4">
              <div>
                <Label>To location</Label>
                <Select value={transferToBranchId} onValueChange={setTransferToBranchId} required>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent>
                    {transferItem && branches.filter((b) => b.id !== transferItem.branch_id).map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min={1}
                  max={transferItem?.current_stock ?? 1}
                  value={transferQty}
                  onChange={(e) => setTransferQty(parseInt(e.target.value, 10) || 0)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setTransferItem(null)}>Cancel</Button>
                <Button type="submit" disabled={transferring || transferQty < 1}>
                  {transferring ? "Transferring…" : "Transfer"}
                </Button>
              </div>
            </form>
          </DialogContent>
      </Dialog>
    </div>
  );
}
